/**
 * Transaction Filters Hook
 * Manages URL-based filter state for transaction list
 *
 * @author SAUBHAGYA Development Team
 * @since Story 6-4
 *
 * @description
 * This hook provides:
 * - URL-based filter state management
 * - Shareable links with filters
 * - Filter update and clear functions
 * - Has-filters detection
 *
 * @example
 * ```tsx
 * const { filters, setFilters, clearFilters, hasFilters } = useTransactionFilters();
 *
 * // Update a single filter
 * setFilters({ status: ['INITIALIZED', 'QC_VERIFIED'] });
 *
 * // Clear all filters
 * clearFilters();
 * ```
 */

import { useSearchParams } from 'react-router-dom';
import { useCallback, useMemo } from 'react';

export interface TransactionFilters {
  /**
   * Selected status filters
   */
  status: string[];

  /**
   * Gaushala search text
   */
  gaushalaSearch: string;

  /**
   * Selected grade filter
   */
  grade: string;

  /**
   * Selected material type ID filter
   */
  materialTypeId: string;

  /**
   * Start date for date range filter (ISO format)
   */
  fromDate: string;

  /**
   * End date for date range filter (ISO format)
   */
  toDate: string;
}

/**
 * Hook to manage transaction filters with URL state
 */
export function useTransactionFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Parse filters from URL search params
   */
  const filters = useMemo((): TransactionFilters => {
    const statusParam = searchParams.get('status');
    const status = statusParam ? statusParam.split(',').filter(Boolean) : [];

    return {
      status,
      gaushalaSearch: searchParams.get('gaushalaSearch') || '',
      grade: searchParams.get('grade') || '',
      materialTypeId: searchParams.get('materialTypeId') || '',
      fromDate: searchParams.get('fromDate') || '',
      toDate: searchParams.get('toDate') || '',
    };
  }, [searchParams]);

  /**
   * Update filters and persist to URL
   */
  const setFilters = useCallback(
    (updates: Partial<TransactionFilters>) => {
      const newParams = new URLSearchParams(searchParams);

      // Update status (array)
      if (updates.status !== undefined) {
        if (updates.status.length > 0) {
          newParams.set('status', updates.status.join(','));
        } else {
          newParams.delete('status');
        }
      }

      // Update text filters
      const textFilters: Array<keyof TransactionFilters> = [
        'gaushalaSearch',
        'grade',
        'materialTypeId',
        'fromDate',
        'toDate',
      ];

      textFilters.forEach((key) => {
        if (updates[key] !== undefined) {
          const value = updates[key];
          if (value && (typeof value === 'string' ? value.trim() : value)) {
            newParams.set(key, String(value));
          } else {
            newParams.delete(key);
          }
        }
      });

      // Reset to first page when filters change
      newParams.delete('page');

      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    const newParams = new URLSearchParams();
    // Preserve page and size if needed
    const page = searchParams.get('page');
    const size = searchParams.get('size');

    if (page) newParams.set('page', page);
    if (size) newParams.set('size', size);

    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  /**
   * Check if any filters are active
   */
  const hasFilters = useMemo(() => {
    return (
      filters.status.length > 0 ||
      filters.gaushalaSearch.trim() !== '' ||
      filters.grade !== '' ||
      filters.materialTypeId !== '' ||
      filters.fromDate !== '' ||
      filters.toDate !== ''
    );
  }, [filters]);

  return {
    filters,
    setFilters,
    clearFilters,
    hasFilters,
  };
}

export default useTransactionFilters;
